/**
 * Mobile design-system tokens + small formatting helpers.
 * Single teal, borders not shadows, no gradients. Used across MobileApp.
 */
export const M = {
  teal: '#0F6E56',
  tealSoft: '#E7F1ED',
  pageBg: '#F8F9FA',
  card: '#FFFFFF',
  textPrimary: '#111827',
  textSecondary: '#6B7280',
  border: '#E5E7EB',
};

// Initials (max 2) for avatars.
export const initials = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Compact INR: ₹2.3L / ₹45.0K / ₹1.2Cr.
export const inr = (n) => {
  const v = Number(n || 0);
  if (!isFinite(v)) return '₹0';
  const abs = Math.abs(v);
  if (abs >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
  if (abs >= 1e5) return `₹${(v / 1e5).toFixed(2)}L`;
  if (abs >= 1e3) return `₹${(v / 1e3).toFixed(1)}K`;
  return `₹${Math.round(v).toLocaleString('en-IN')}`;
};

// Full INR with separators for detail screens.
export const inrFull = (n) => `₹${Math.round(Number(n || 0)).toLocaleString('en-IN')}`;

const d = (s) => { if (!s) return null; const dt = new Date(s); return isNaN(dt.getTime()) ? null : dt; };
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const dateRange = (start, end) => {
  const a = d(start); const b = d(end);
  if (!a && !b) return '';
  if (a && !b) return `${a.getDate()} ${MON[a.getMonth()]}`;
  if (!a && b) return `${b.getDate()} ${MON[b.getMonth()]}`;
  if (a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()) {
    return `${a.getDate()}–${b.getDate()} ${MON[a.getMonth()]}`;
  }
  return `${a.getDate()} ${MON[a.getMonth()]} – ${b.getDate()} ${MON[b.getMonth()]}`;
};

export const pax = (t) => (Number(t.adults || 0) + Number(t.children || 0) + Number(t.infants || 0));

// Friendly trip-stage label mapped over the real status (decision: map, not rename).
export const STAGE = {
  draft: { label: 'Enquiry', cls: 'bg-slate-100 text-slate-600' },
  sent: { label: 'Quote', cls: 'bg-amber-50 text-amber-700' },
  confirmed: { label: 'Booked', cls: 'bg-[#E7F1ED] text-[#0F6E56]' },
  accepted: { label: 'Booked', cls: 'bg-[#E7F1ED] text-[#0F6E56]' },
  ongoing: { label: 'Ongoing', cls: 'bg-blue-50 text-blue-700' },
  completed: { label: 'Completed', cls: 'bg-emerald-50 text-emerald-700' },
  cancelled: { label: 'Cancelled', cls: 'bg-rose-50 text-rose-700' },
};

export const stageKey = (trip) => {
  const st = (trip.status || 'draft').toLowerCase();
  if (st === 'cancelled') return 'cancelled';
  if (st === 'completed') return 'completed';
  if (['confirmed', 'accepted'].includes(st)) {
    const a = d(trip.travelStartDate); const b = d(trip.travelEndDate) || a;
    const now = new Date(); const today = new Date(); today.setHours(0, 0, 0, 0);
    if (a && b && a <= now && b >= today) return 'ongoing';
    return 'confirmed';
  }
  if (st === 'sent') return 'quote_real'; // sentinel handled below
  return 'draft';
};

export const stageMeta = (trip) => {
  const st = (trip.status || 'draft').toLowerCase();
  if (st === 'sent') return STAGE.sent;
  const k = stageKey(trip);
  return STAGE[k] || STAGE.draft;
};

// Outstanding from the trip itself: total - received (sum of receipts).
export const tripMoney = (trip) => {
  const total = Number(trip.totalCost || 0);
  const received = (trip.receipts || []).reduce((s, r) => s + Number(r.amount ?? r.amountPaid ?? 0), 0);
  const outstanding = Math.max(total - received, 0);
  return { total, received, outstanding };
};

// Sum of amountPaid on this trip's NON-cancelled/converted invoices. Matches
// either the canonical tripId or the displayTripId so payments recorded against
// an invoice are counted toward the trip (not just trip.receipts).
export const tripInvoicePaid = (trip, invoices = []) => {
  if (!trip || !Array.isArray(invoices) || invoices.length === 0) return 0;
  let paid = 0;
  for (const inv of invoices) {
    const st = (inv.status || '').toLowerCase();
    if (st === 'cancelled' || st === 'converted') continue;
    const match = (trip.tripId && inv.tripId === trip.tripId) ||
      (trip.displayTripId && inv.displayTripId === trip.displayTripId);
    if (match) paid += Number(inv.amountPaid || 0);
  }
  return paid;
};

// Outstanding amount that is the single source of truth for "payment pending":
// trip value minus EVERYTHING received (trip receipts + invoice payments).
// Invoice STATUS is ignored — only the paid amount matters.
export const tripOutstanding = (trip, invoices = []) => {
  const total = Number(trip.totalCost || 0);
  const recs = (trip.receipts || []).reduce((s, r) => s + Number(r.amount ?? r.amountPaid ?? 0), 0);
  const received = recs + tripInvoicePaid(trip, invoices);
  return Math.max(total - received, 0);
};

// A trip only counts as "payment pending" once it is a real booking
// (confirmed / accepted onwards) AND still has an outstanding balance.
// Draft enquiries and unsent/sent quotes are NOT payment-pending.
export const PAYMENT_PENDING_STATUSES = new Set(['confirmed', 'accepted', 'ongoing', 'completed']);
export const isPaymentPending = (trip, invoices = []) => {
  const st = (trip.status || '').toLowerCase();
  return PAYMENT_PENDING_STATUSES.has(st) && tripOutstanding(trip, invoices) > 0;
};

// Outstanding pill tone: green=settled, red=overdue, amber=pending.
export const outstandingTone = (trip) => {
  const { total, outstanding } = tripMoney(trip);
  if (total <= 0) return null;
  if (outstanding <= 0) return 'paid';
  // Overdue if any schedule item is past-due.
  const today = new Date().toISOString().slice(0, 10);
  const overdue = (trip.paymentSchedule || []).some((p) => {
    const due = (p.dueDate || '').slice(0, 10);
    const settled = (p.status || '').toLowerCase() === 'paid';
    return due && due < today && !settled;
  });
  return overdue ? 'overdue' : 'pending';
};

export const OUTSTANDING = {
  paid: { label: 'Paid', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  pending: { label: 'Pending', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  overdue: { label: 'Overdue', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
};

// Build a wa.me number from countryCode + phone. Re-exports the single shared
// normalizer (utils/phone.js) so mobile + desktop dedupe identically.
export { waNumber } from '@/utils/phone';
